import { useMutationDeleteCancellationReason } from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { CancellationReasonDTO } from "@gymflow/types";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Button, EditIcon, TrashIcon } from "../../../atoms";

type CancellationReasonItemProps = {
  isEditing: boolean;
  onStartEditReason: (item: CancellationReasonDTO) => Promise<void>;
  reasonItem: CancellationReasonDTO;
};

export const CancellationReasonItem = ({
  reasonItem,
  isEditing,
  onStartEditReason,
}: CancellationReasonItemProps) => {
  const { api } = useGymflowModels();
  const { clubId } = useClubSettings();
  const { notifyDanger } = useContext(ToastContext);
  const { t } = useTranslation();

  const { mutateAsync: mutateAsyncDeleteCancellationReason } =
    useMutationDeleteCancellationReason({ api });

  const onClickDelete = useCallback(async () => {
    try {
      await mutateAsyncDeleteCancellationReason({
        clubId,
        reasonId: reasonItem.id,
      });
    } catch (e) {
      if (isAxiosError(e)) {
        notifyDanger(e);
      }
    }
  }, [
    clubId,
    mutateAsyncDeleteCancellationReason,
    notifyDanger,
    reasonItem.id,
  ]);

  const onClickEdit = useCallback(async () => {
    try {
      onStartEditReason(reasonItem);
    } catch (e) {
      if (isAxiosError(e)) {
        notifyDanger(e);
      }
    }
  }, [notifyDanger, onStartEditReason, reasonItem]);

  return (
    <div className="flex flex-row items-center justify-between border-b border-b-gray-300 last:border-none">
      <div
        className={cn("text-base font-medium text-gray-950", {
          "text-gray-500 py-2.5": isEditing,
        })}
      >
        {reasonItem.name}
      </div>
      <div className="flex flex-row gap-1">
        {isEditing ? (
          <div className="text-base font-medium text-gray-500">
            {t(
              "components.cancellationReasonsModal.cancellationReasonItem.editing",
            )}
          </div>
        ) : (
          <>
            <Button
              intent="transparent"
              className="p-2"
              onClick={async () => await onClickEdit()}
            >
              <EditIcon className="size-6" pathClassName="stroke-gray-500" />
            </Button>
            <Button
              intent="transparent"
              className="p-2"
              onClick={async () => await onClickDelete()}
            >
              <TrashIcon className="size-6" pathClassName="stroke-error-600" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
